import { Box } from "@mui/material";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import SvgToIcon from "../../../../../../../../icons/SvgToIcon";
import { ActivityLogType } from "../../../../../../../../utils/constants";
import { getPrettyDateTime } from "../../../../../../../../utils/timestamp";
import { severities } from "../../../../../../../stack/environment/icons/iconExport";
import { CustomLogTypography, EntityLink } from "./utils";

const MoreEntitiesText = ({ otherEntitiesCount }) => {
  return (
    <CustomLogTypography>
      and{" "}
      <CustomLogTypography sx={{ fontWeight: 500 }}>
        {otherEntitiesCount} more
      </CustomLogTypography>
    </CustomLogTypography>
  );
};

const ReasonText = ({ children }) => {
  if (!children) return null;
  const maxReasonSize = 20;
  return (
    <CustomLogTypography>
      . Reason:{" "}
      <CustomTooltip
        title={children.length > maxReasonSize ? children : null}
        placement="top"
        arrow
        disableInteractive
      >
        <Box display="inline-block">
          <CustomLogTypography>
            {children.length > maxReasonSize
              ? children.slice(0, maxReasonSize) + "..."
              : children}
          </CustomLogTypography>
        </Box>
      </CustomTooltip>
    </CustomLogTypography>
  );
};

export default function getUserActionLogContent(log) {
  if (log.log_type === ActivityLogType.EXCLUDED_ENTITIES) {
    const firstEntityId = Object.keys(log.entity_names_by_id)[0];
    const firstEntityName = log.entity_names_by_id[firstEntityId];
    const otherEntitiesCount = Object.keys(log.entity_names_by_id).length - 1;

    const moreEntitiesText =
      otherEntitiesCount > 0 ? (
        <MoreEntitiesText otherEntitiesCount={otherEntitiesCount} />
      ) : (
        ""
      );

    if (log.all_entities) {
      return (
        <CustomLogTypography>
          excluded all misconfigurations<ReasonText>{log.reason}</ReasonText>
        </CustomLogTypography>
      );
    }

    return (
      <CustomLogTypography>
        excluded{" "}
        <EntityLink log={log} entityId={firstEntityId}>
          {firstEntityName}
        </EntityLink>{" "}
        {moreEntitiesText} from analysis<ReasonText>{log.reason}</ReasonText>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.INCLUDED_ENTITIES) {
    const firstEntityId = Object.keys(log.entity_names_by_id)[0];
    const firstEntityName = log.entity_names_by_id[firstEntityId];
    const otherEntitiesCount = Object.keys(log.entity_names_by_id).length - 1;

    const moreEntitiesText =
      otherEntitiesCount > 0 ? (
        <MoreEntitiesText otherEntitiesCount={otherEntitiesCount} />
      ) : (
        ""
      );

    if (log.all_entities) {
      return (
        <CustomLogTypography>
          included all misconfigurations
        </CustomLogTypography>
      );
    }

    return (
      <CustomLogTypography>
        included{" "}
        <EntityLink log={log} entityId={firstEntityId}>
          {firstEntityName}
        </EntityLink>{" "}
        {moreEntitiesText} for analysis
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.CONTROL_SNOOZED) {
    return (
      <CustomLogTypography>
        snoozed this control for{" "}
        {log.days_to_snooze <= 1 ? "a day" : `${log.days_to_snooze} days`}
        <ReasonText>{log.reason}</ReasonText>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.CONTROL_UNSNOOZED)
    return <CustomLogTypography>unsnoozed this control</CustomLogTypography>;

  if (log.log_type === ActivityLogType.SCAN)
    return (
      <CustomLogTypography>
        initiated a scan for this control
      </CustomLogTypography>
    );

  if (log.log_type === ActivityLogType.SNOOZED_ENTITIES) {
    const firstEntityId = Object.keys(log.entity_names_by_id)[0];
    const firstEntityName = log.entity_names_by_id[firstEntityId];
    const otherEntitiesCount = Object.keys(log.entity_names_by_id).length - 1;

    const moreEntitiesText =
      otherEntitiesCount > 0 ? (
        <MoreEntitiesText otherEntitiesCount={otherEntitiesCount} />
      ) : (
        ""
      );

    return (
      <CustomLogTypography>
        snoozed{" "}
        <EntityLink log={log} entityId={firstEntityId}>
          {firstEntityName}
        </EntityLink>{" "}
        {moreEntitiesText} for {log.snooze_period_days} days
        <ReasonText>{log.reason}</ReasonText>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.UNSNOOZED_ENTITIES) {
    const firstEntityId = Object.keys(log.entity_names_by_id)[0];
    const firstEntityName = log.entity_names_by_id[firstEntityId];
    const otherEntitiesCount = Object.keys(log.entity_names_by_id).length - 1;

    const moreEntitiesText =
      otherEntitiesCount > 0 ? (
        <MoreEntitiesText otherEntitiesCount={otherEntitiesCount} />
      ) : (
        ""
      );

    return (
      <CustomLogTypography>
        unsnoozed{" "}
        <EntityLink log={log} entityId={firstEntityId}>
          {firstEntityName}
        </EntityLink>{" "}
        {moreEntitiesText}
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.ASSIGNED)
    return (
      <CustomLogTypography>
        assigned this control to{" "}
        <CustomLogTypography sx={{ fontWeight: 500 }}>
          {log.assignee_name}
        </CustomLogTypography>
      </CustomLogTypography>
    );

  if (log.log_type === ActivityLogType.UNASSIGNED)
    return <CustomLogTypography>removed assignee</CustomLogTypography>;

  if (log.log_type === ActivityLogType.SEVERITY_RAISED)
    return (
      <CustomLogTypography>
        raised severity from {log.previous_severity} to {log.new_severity}
      </CustomLogTypography>
    );

  if (log.log_type === ActivityLogType.SEVERITY_LOWERED)
    return (
      <CustomLogTypography>
        lowered severity from {log.previous_severity} to {log.new_severity}
      </CustomLogTypography>
    );

  if (log.log_type === ActivityLogType.REMEDIATION_PRIORITY_RAISED) {
    return (
      <CustomLogTypography>
        raised remediation priority from {log.previous_remediation_priority} to{" "}
        {log.new_remediation_priority}
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.REMEDIATION_PRIORITY_LOWERED)
    return (
      <CustomLogTypography>
        lowered remediation priority from {log.previous_remediation_priority} to{" "}
        {log.new_remediation_priority}
      </CustomLogTypography>
    );

  if (log.log_type === ActivityLogType.DETECTION_RESOLVED)
    return (
      <Box display="inline-flex" alignItems="center" gap="4px">
        <CustomLogTypography>resolved a detected event</CustomLogTypography>
        <Box position="relative" top="1px">
          <CustomTooltip
            title={`${log.detection_title}, ${getPrettyDateTime(
              log.detection_timestamp
            )}`}
            placement="top"
          >
            <span>
              <SvgToIcon
                IconLogo={severities.SeverityInfoTooltipIcon}
                width="12px"
                height="12px"
              />
            </span>
          </CustomTooltip>
        </Box>
      </Box>
    );

  if (log.log_type === ActivityLogType.AUTO_REMEDIATION) {
    const firstEntityId = Object.keys(log.entity_names_by_id)[0];
    const firstEntityName = log.entity_names_by_id[firstEntityId];
    const otherEntitiesCount = Object.keys(log.entity_names_by_id).length - 1;

    const moreEntitiesText =
      otherEntitiesCount > 0 ? (
        <MoreEntitiesText otherEntitiesCount={otherEntitiesCount} />
      ) : (
        ""
      );

    return (
      <CustomLogTypography>
        auto remediated{" "}
        <EntityLink log={log} entityId={firstEntityId}>
          {firstEntityName}
        </EntityLink>{" "}
        {moreEntitiesText}
        <ReasonText>{log.reason}</ReasonText>
      </CustomLogTypography>
    );
  }
  return null;
}
